import {
    useMutation,
    UseMutationOptions,
    QueryClient,
} from '@tanstack/react-query';
import { toast, Id } from 'react-toastify';
import type { ApiErrorResponse, ApiSuccessResponse } from '../types/api';
import { api, ApiRequest } from '~/components/Providers/APIClient';
import { useRouteContext, useRouter } from '@tanstack/react-router';
import { useLocaleDateTimeString } from './useLocale';
import { useAuthData } from '~/components/Providers/AuthProvider';
import { PublicAuthData } from '~/auth/session';

type LocaleString = (data: string | number | Date) => string;
type QueryKeys = string[];

type ToastMessage<TToastFunction> = undefined | string | TToastFunction;

type LoadingToastMessage<TVariables> = ToastMessage<
    (variables: TVariables) => string
>;
type ErrorToastMessage<TVariables> = ToastMessage<
    ({
        variables,
        error,
        localeDateTimeString,
    }: {
        variables: TVariables;
        error: ApiErrorResponse | Error;
        localeDateTimeString: LocaleString;
    }) => string
>;
type SuccessToastMessage<TVariables, TData> = ToastMessage<
    ({
        variables,
        response,
        localeDateTimeString,
    }: {
        variables: TVariables;
        response: ApiSuccessResponse<TData>;
        localeDateTimeString: LocaleString;
    }) => string
>;

type ToastMessages<TData, TVariables> = {
    loading: LoadingToastMessage<TVariables>;
    error: ErrorToastMessage<TVariables>;
    success: SuccessToastMessage<TVariables, TData>;
};

type customUpdateCache<TData, TVariables> = ({
    queryClient,
    response,
    variables,
}: {
    queryClient: QueryClient;
    response: ApiSuccessResponse<TData>;
    variables: TVariables;
}) => Promise<void> | void;

type invalidateQueriesOptions<TData, TVariables> =
    | QueryKeys
    | QueryKeys[]
    | (({
          response,
          variables,
      }: {
          response: ApiSuccessResponse<TData>;
          variables: TVariables;
      }) => QueryKeys | QueryKeys[] | Promise<QueryKeys | QueryKeys[]>);

type MutationOptionsWithRequestBuilder<TVariables> = {
    buildRequest: (variables: TVariables) => ApiRequest;
};

type MutationOptionsWithCustomMutationFn<TData, TVariables> = {
    customMutationFn: (
        variables: TVariables,
        queryClient: QueryClient,
        authData: Partial<PublicAuthData> | null,
    ) => Promise<ApiSuccessResponse<TData>>;
};

type MutationOptionsBaseRequest<TData, TVariables> =
    | MutationOptionsWithRequestBuilder<TVariables>
    | MutationOptionsWithCustomMutationFn<TData, TVariables>;

type MutationOptionsWithInvalidateQueries<TData, TVariables> = {
    invalidateQueries: invalidateQueriesOptions<TData, TVariables>;
    customUpdateCache?: never;
};

type MutationOptionsWithCustomCacheUpdate<TData, TVariables> = {
    customUpdateCache: customUpdateCache<TData, TVariables>;
    invalidateQueries?: never;
};

type MutationOptionsWithoutCacheUpdate = {
    customUpdateCache?: never;
    invalidateQueries?: never;
};

type MutationOptionsBase<TData, TVariables> = MutationOptionsBaseRequest<
    TData,
    TVariables
> &
    (
        | MutationOptionsWithoutCacheUpdate
        | MutationOptionsWithInvalidateQueries<TData, TVariables>
        | MutationOptionsWithCustomCacheUpdate<TData, TVariables>
    );

export type ApiMutationOptions<TData, TVariables> = MutationOptionsBase<
    TData,
    TVariables
> & {
    toastMessages: ToastMessages<TData, TVariables>;
} & UseMutationOptions<
        ApiSuccessResponse<TData>,
        ApiErrorResponse | Error,
        TVariables,
        { toastId?: Id }
    >;

export function useApiMutation<TData, TVariables>(
    options: ApiMutationOptions<TData, TVariables>,
) {
    const router = useRouter();
    const localeDateTimeString = useLocaleDateTimeString();
    const { queryClient } = useRouteContext({ from: '__root__' });

    const authedUser = useAuthData();

    return useMutation<
        ApiSuccessResponse<TData>,
        ApiErrorResponse | Error,
        TVariables,
        { toastId?: Id }
    >({
        mutationFn: async (variables) => {
            if ('buildRequest' in options) {
                const request = options.buildRequest(variables);
                const response = await api.send<TData>(request);
                if (response.error) {
                    throw response;
                }
                return response;
            } else if ('customMutationFn' in options) {
                return await options.customMutationFn(
                    variables,
                    queryClient,
                    authedUser,
                );
            }
            throw new Error('Invalid mutation options');
        },
        onMutate: async (variables) => {
            const {
                toastMessages: { loading },
            } = options;
            if (!loading) return {};
            const toastId = toast.loading(
                typeof loading === 'function' ? loading(variables) : loading,
                {
                    autoClose: 10000,
                },
            );
            return { toastId };
        },

        onSuccess: async (
            response,
            variables,
            onMutateResult,
            mutationContext,
        ) => {
            const {
                toastMessages: { success },
                customUpdateCache,
                invalidateQueries,
            } = options;

            if (customUpdateCache || invalidateQueries) {
                if (invalidateQueries) {
                    const queryKeys =
                        typeof invalidateQueries === 'function'
                            ? await invalidateQueries({
                                  response,
                                  variables,
                              })
                            : invalidateQueries;

                    const queryKeyGroups = (
                        Array.isArray(queryKeys[0]) ? queryKeys : [queryKeys]
                    ) as QueryKeys[];

                    await Promise.all(
                        queryKeyGroups.map((queryKey) =>
                            queryClient.invalidateQueries({ queryKey }),
                        ),
                    );
                } else if (customUpdateCache) {
                    await customUpdateCache({
                        queryClient,
                        response,
                        variables,
                    });
                }
                await router.invalidate({ sync: true });
            }

            if (onMutateResult?.toastId && success) {
                toast.update(onMutateResult.toastId, {
                    render:
                        typeof success === 'function'
                            ? success({
                                  variables,
                                  response,
                                  localeDateTimeString,
                              })
                            : success,
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000,
                });
            }

            options.onSuccess?.(
                response,
                variables,
                onMutateResult,
                mutationContext,
            );
        },

        onError: (error, variables, onMutateResult, mutationContext) => {
            const {
                toastMessages: { error: toastMessagesError },
            } = options;
            if (onMutateResult?.toastId && toastMessagesError)
                toast.update(onMutateResult.toastId, {
                    render:
                        typeof toastMessagesError === 'function'
                            ? toastMessagesError({
                                  variables,
                                  error,
                                  localeDateTimeString,
                              })
                            : toastMessagesError +
                              ('data' in error
                                  ? (error.data?.message ?? 'Unknown error')
                                  : (error?.message ?? 'Unknown error')),
                    type: 'error',
                    isLoading: false,
                    autoClose: 10000,
                });

            options.onError?.(
                error,
                variables,
                onMutateResult,
                mutationContext,
            );
        },
    });
}
